import { useLocalStorage } from '@cheqroom/hooks';
import { useEffect, useMemo, useRef } from 'react';

import useOverviewConfig, {
	getDefaultViewConfigWithInlineFields,
} from '../../components/Overview/use-overview-config.hook';
import { useAuth } from '../../context/Authentication.context';
import useItemTypes from '../item-types/use-item-types';
import { FieldType, SystemFieldType } from './types';

export const EXCLUDE_ITEM_FIELDS = ['name', 'isRestricted'];

const CUSTOMIZE_ITEM_VIEW_DEFAULT_SELECTION = [SystemFieldType.barcode, SystemFieldType.qr, SystemFieldType.created];
const LEGACY_STORAGE_KEY_SUFFIX = 'app.item-view';

// Maps old field IDs to new SystemFieldType enum values
const LEGACY_FIELD_MAPPING: Record<string, FieldType> = {
	qr: SystemFieldType.qr, // 'qr' -> 'codes'
	barcode: SystemFieldType.barcode, // 'barcode' -> 'barcodes'
} as const;

type SaveItemFieldIds = (ids: FieldType[]) => void;

/**
 * Manages item field configuration for items overview.
 *
 * Reads and writes the overview config storage (namespaced by itemTypeId) and
 * migrates the selection from the legacy localStorage key on first use, mapping
 * old field IDs onto the current enum values.
 *
 * @param itemTypeId - The item type ID to namespace the config
 * @returns Tuple of [selectedFieldIds, saveFunction]
 */
export const useItemFieldsConfig = (itemTypeId?: string): [FieldType[], SaveItemFieldIds] => {
	const { id: userId } = useAuth();

	const { getDefaultItemType } = useItemTypes();
	const defaultItemType = getDefaultItemType();

	const [itemsConfig, setOverviewConfig] = useOverviewConfig<FieldType>({
		type: 'items-list',
		namespace: itemTypeId ?? defaultItemType?.id ?? 'all',
		view: 'table',
		defaultConfig: getDefaultViewConfigWithInlineFields<FieldType>(
			'items-list',
			CUSTOMIZE_ITEM_VIEW_DEFAULT_SELECTION
		),
	});

	const storageKey = `${userId ?? ''}${LEGACY_STORAGE_KEY_SUFFIX}`;
	const [selectedFieldIds] = useLocalStorage<FieldType[]>(storageKey, CUSTOMIZE_ITEM_VIEW_DEFAULT_SELECTION);

	const migrationAttemptedRef = useRef(false);

	const currentInlineFields = useMemo(() => itemsConfig?.inlineFields ?? [], [itemsConfig?.inlineFields]);

	// Migrate from legacy storage to new storage system
	useEffect(() => {
		if (migrationAttemptedRef.current || !userId || !storageKey || !selectedFieldIds) {
			return;
		}

		const hasLegacyData = selectedFieldIds.length > 0;
		const hasNewData = currentInlineFields.length > 0;
		const shouldMigrate = !hasNewData && hasLegacyData;

		if (shouldMigrate) {
			migrationAttemptedRef.current = true;
			setOverviewConfig({
				...itemsConfig,
				inlineFields: selectedFieldIds.map((id) => LEGACY_FIELD_MAPPING[id] ?? id),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentInlineFields.length, selectedFieldIds, userId, storageKey]);

	// Save function that writes to the new storage system
	const saveToNewStorage: SaveItemFieldIds = (ids: FieldType[]) => {
		setOverviewConfig({
			...itemsConfig,
			inlineFields: ids,
		});
	};

	return [currentInlineFields.filter((field) => !EXCLUDE_ITEM_FIELDS.includes(field)), saveToNewStorage];
};

export default useItemFieldsConfig;
